import {
  FC,
  PropsWithChildren,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react'
import Scheduler, { Resource, SchedulerTypes } from 'devextreme-react/scheduler'
import { useUserSetting } from '@crew/states'
import { Region, SettingKeyType } from '@crew/enums/app'
import { useScheduleWeekly } from './useScheduleWeekly'
import { DateCell } from './components/dateCell/dateCell'
import { DataCell } from './components/dataCell/dataCell'
import { useTranslation } from '@crew/modules/i18n'
import { Appointment } from './components/appointment/appointment'
import {
  CrewEventPopover,
  EventPopover,
} from 'components/elements/crewEventPopover/crewEventPopover'
import { useAppSelector } from 'states/hooks'
import { useModal } from 'components/layouts/modal/useModal'
import dayjs from '@crew/modules'
import { JsonDateFormat } from '@crew/enums/system'
import { ContentReadyEvent, FirstDayOfWeek } from 'devextreme/ui/scheduler'
import { useValueChangeEffect } from '@crew/hooks'
import { EventKindRef, UserRef, ProjectRef } from '@crew/models/refs'
import { EventScheduleEntryDialog } from 'features/event/components/eventScheduleEntryDialog/eventScheduleEntryDialog'
import { ProjectType } from '@crew/enums/domain'
import { CrewTimeCell } from 'components/devextreme/crewScheduler/components/crewTimeCell/crewTimeCell'
import { CrewScheduler } from 'components/devextreme/crewScheduler/crewScheduler'
import {
  CrewBadgeInvertedColorClass,
  CrewBadgeInvertedColorToHex,
} from 'enums/color'

type Event = {
  id: string | null
  subject: string | null
  description: string | null
  startDatetime: string
  endDatetime: string
  entityRecordId: string | null
  eventKind: EventKindRef | null
  eventAttendees: UserRef[]
  project: ProjectRef
  projectType: ProjectType
  isAllDay: boolean
}

type AppointmentType = {
  data: { appointmentData: Event }
}

// Render time cell
const renderTimeCell = ({ date, text }: { date: Date; text: string }) => {
  return <CrewTimeCell date={date} displayTime={Boolean(text)} />
}

// Get the first day of the week from the current date
const firstDayOfWeek = new Date().getDay() as FirstDayOfWeek

export const ScheduleWeekly: FC = memo(() => {
  const { t } = useTranslation()

  const schedulerRef = useRef<Scheduler>(null)

  const [currentDate, setCurrentDate] = useState(new Date())

  const [isEventEntryDialogOpen, openEventEntryDialog, closeEventEntryDialog] =
    useModal()

  const { eventKindDataSource, eventDataSource } =
    useScheduleWeekly(currentDate)

  const eventEventMessage = useAppSelector(
    (state) => state.app.eventEventMessage
  )

  // ユーザー設定からデフォルトのユーザープロファイル地域を取得
  const defaultUserProfileRegion = useUserSetting(
    SettingKeyType.UserProfileRegion,
    Region.Japan.value
  )

  useValueChangeEffect(
    () => {
      eventDataSource.reload()
    },
    [eventEventMessage, eventDataSource],
    eventEventMessage
  )

  // Customize date navigator display text
  const customizeDateNavigatorText = useCallback(
    (info: SchedulerTypes.DateNavigatorTextInfo) => {
      const startDate = t('format.dateWithText', {
        value: info.startDate,
      })

      const endDate = t('format.dateWithText', {
        value: info.endDate,
      })
      return `${startDate} - ${endDate}`
    },
    [t]
  )

  // Event handler for when the current date is changed
  const handleCurrentDateChange = useCallback(
    (value: string | number | Date) => {
      setCurrentDate(new Date(value))
    },
    []
  )

  // Event handler for when an appointment is rendered
  const handleAppointmentRendered = useCallback(
    (e: SchedulerTypes.AppointmentRenderedEvent) => {
      // If is private event (id is null), set the background color to gray
      if (!e.appointmentData.id) {
        e.appointmentElement.style.backgroundColor = '#d4d4d4'
        // Add disabled class
        e.appointmentElement.className += ' dx-state-disabled'
      } else {
        if (e.appointmentData.eventKind) {
          // Set the background color to the event color
          e.appointmentElement.style.backgroundColor =
            CrewBadgeInvertedColorToHex[
              e.appointmentData.eventKind
                .displayColor as CrewBadgeInvertedColorClass
            ]
        }
      }
    },
    []
  )

  // Event handler for when an appointment is clicked
  const handleAppointmentClick = useCallback(
    (e: SchedulerTypes.AppointmentClickEvent) => {
      // If is private event (id is null), cancel the event
      if (!e.appointmentData.id) {
        e.cancel = true
      }
    },
    []
  )

  // Event handler for when an appointment is double clicked
  const handleAppointmentDblClick = useCallback(
    (e: SchedulerTypes.AppointmentDblClickEvent) => {
      e.cancel = true

      // If is private event (id is null), cancel the event
      if (!e.appointmentData.id) return
    },
    []
  )

  // close appointment tooltip
  const handleCloseTooltip = useCallback(() => {
    schedulerRef.current?.instance.hideAppointmentTooltip()
  }, [])

  // Render appointment tooltip
  const renderAppointmentTooltip = useCallback(
    (item: AppointmentType) => {
      const data: EventPopover = {
        id: item.data.appointmentData.id,
        description: item.data.appointmentData.description,
        attendees: item.data.appointmentData.eventAttendees,
        startDatetime: item.data.appointmentData.startDatetime,
        endDatetime: item.data.appointmentData.endDatetime,
        entityRecordId: item.data.appointmentData.entityRecordId,
        eventKind: item.data.appointmentData.eventKind,
        subject: item.data.appointmentData.subject,
        project: item.data.appointmentData.project,
        isAllDay: item.data.appointmentData.isAllDay,
      }
      return (
        <CrewEventPopover data={data} onCloseTooltip={handleCloseTooltip} />
      )
    },
    [handleCloseTooltip]
  )

  // Register event finish
  const handleEventRegistered = useCallback(() => {
    // Close entry dialog
    closeEventEntryDialog()
  }, [closeEventEntryDialog])

  const startDatetime = useRef<Date>()
  const endDatetime = useRef<Date>()

  // 空欄をクリックすると新規イベント登録画面を表示する。
  const handleOpenEventEntryDialog = useCallback(
    (e: SchedulerTypes.AppointmentFormOpeningEvent) => {
      e.cancel = true

      // 開始日/終了日=ドラッグして選択した時間
      // 開始時刻/終了時刻=ドラッグして選択した時間
      startDatetime.current = new Date(
        dayjs(e.appointmentData?.startDatetime)
          .tz(String(defaultUserProfileRegion))
          .format(JsonDateFormat.YYYYMMDDHHmmss)
      )
      endDatetime.current = new Date(
        dayjs(e.appointmentData?.endDatetime)
          .tz(String(defaultUserProfileRegion))
          .format(JsonDateFormat.YYYYMMDDHHmmss)
      )

      // Open crew event entry dialog
      openEventEntryDialog()
    },
    [defaultUserProfileRegion, openEventEntryDialog]
  )

  // Render date cell
  const renderDateCell = useCallback(({ date }: { date: Date }) => {
    return <DateCell date={date} />
  }, [])

  // render data cell
  const renderDataCell = useCallback(
    (
      props: PropsWithChildren<{
        data: {
          startDate: Date
        }
      }>
    ) => {
      return <DataCell {...props} />
    },
    []
  )

  const [schedulerWidth, setSchedulerWidth] = useState<number>()

  // Listen layout change to update schedule width
  // Cell item position/width will be recalculated by the devextreme after update schedule width
  const schedulerWrapperRef = useCallback((node: HTMLDivElement) => {
    if (!node) return
    const resizeObserver = new ResizeObserver(() =>
      setSchedulerWidth(node.offsetWidth)
    )
    resizeObserver.observe(node)
  }, [])

  // Event handler for when the content is ready
  // https://supportcenter.devexpress.com/ticket/details/t958543/scheduler-customize-toolbar
  const onContentReady = useCallback(
    (e: ContentReadyEvent) => {
      // find group header
      const allDayHeaderElement = e.element.querySelector(
        '.dx-scheduler-header-panel-empty-cell .dx-scheduler-all-day-title'
      )

      if (allDayHeaderElement) {
        // set all day header text
        allDayHeaderElement.textContent = t('label.allDay')
        allDayHeaderElement.classList.add('font-normal')
      }

      const toolbarAfterElement = e.element.querySelector('.dx-toolbar-after')
      toolbarAfterElement?.classList.add('d-none')
    },
    [t]
  )

  // Appointment collector render
  const handleAppointmentCollectorRender = useCallback(
    (data: SchedulerTypes.AppointmentCollectorTemplateData) => {
      return (
        <div className="crew-link">
          {t('label.selectMore', {
            value: data.appointmentCount,
          })}
        </div>
      )
    },
    [t]
  )

  return (
    <div className="flex-1 overflow-auto" ref={schedulerWrapperRef}>
      <CrewScheduler
        id="projectDetailScheduler"
        // Set the `custom-scheduler` class  to display Scheduler like Figma
        className="custom-scheduler"
        // DBに保存されている日時はUTC時間であるため、スケジューラが自動的にクライアントのタイムゾーンに変換できるようにタイムゾーンを指定する必要がある。
        timeZone={defaultUserProfileRegion as string}
        ref={schedulerRef}
        dataSource={eventDataSource}
        defaultCurrentView="week"
        defaultCurrentDate={currentDate}
        currentView={'week'}
        startDateExpr="startDatetime"
        endDateExpr="endDatetime"
        dateCellRender={renderDateCell}
        dataCellComponent={renderDataCell}
        onCurrentDateChange={handleCurrentDateChange}
        customizeDateNavigatorText={customizeDateNavigatorText}
        showCurrentTimeIndicator={false}
        appointmentComponent={Appointment}
        onAppointmentRendered={handleAppointmentRendered}
        onAppointmentClick={handleAppointmentClick}
        onAppointmentDblClick={handleAppointmentDblClick}
        height="100%"
        appointmentTooltipComponent={renderAppointmentTooltip}
        onAppointmentFormOpening={handleOpenEventEntryDialog}
        allDayExpr="isAllDay"
        width={schedulerWidth}
        onContentReady={onContentReady}
        timeCellRender={renderTimeCell}
        firstDayOfWeek={firstDayOfWeek}
        appointmentCollectorRender={handleAppointmentCollectorRender}
      >
        <Resource
          dataSource={eventKindDataSource}
          fieldExpr="eventKind.id"
          useColorAsDefault={true}
          colorExpr="displayColor"
        />
      </CrewScheduler>

      {/* Event new schedule */}
      <EventScheduleEntryDialog
        isEditMode={false}
        title={t('label.registerNewSchedule')}
        isOpen={isEventEntryDialogOpen}
        onClose={closeEventEntryDialog}
        onSubmit={handleEventRegistered}
        startDatetime={startDatetime.current}
        endDatetime={endDatetime.current}
      />
    </div>
  )
})
